import React, { useEffect, useMemo, useState } from "react";
import { Button, Drawer, Table, Tag, Tooltip } from "antd";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { fetchDrill } from "../../service/partnerReports";
import { areaColor, downloadText, money, toCsv } from "./charts";

// "Click any number and see the records behind it." One drawer serves every
// panel: the caller says which kind of records and any narrowing (attorney,
// practice area, month), the drawer fetches and lists them. Matters link
// straight to the matter page — the one thing Tableau cannot do.

export const KIND_TITLES = {
  signups: "Matters opened",
  open_caseload: "Open matters",
  time_to_matter: "Matters opened — time from first contact",
  converted: "Intakes that signed",
  turned_down: "Intakes turned down",
  archived: "Intakes archived",
  referred_out: "Intakes referred out",
  in_progress: "Intakes still being worked",
  waiting: "Prospects waiting right now",
  costs_advanced: "Matters with costs advanced",
  costs_recovered: "Matters with costs recovered",
  costs_outstanding: "Open matters with unpaid costs",
  costs_at_risk: "Open matters 24+ months old with unpaid costs",
};

const fmtDate = (iso) => (iso ? dayjs(iso).format("D MMM YYYY") : "—");

export async function fetchAllDrillRows(request, filters, firstPage) {
  const rows = [...(firstPage.rows || [])];
  const total = firstPage.total || rows.length;
  const limit = firstPage.limit || rows.length || 1;
  let offset = rows.length;
  while (rows.length < total) {
    // eslint-disable-next-line no-await-in-loop
    const res = await fetchDrill(request.kind, filters, { ...(request.extra || {}), offset });
    const page = res.data?.rows || [];
    if (!page.length) break;
    rows.push(...page);
    offset += limit;
  }
  return rows;
}

export default function DrillDrawer({ request, filters, onClose }) {
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: false, data: null, error: null });

  useEffect(() => {
    if (!request) return undefined;
    let cancelled = false;
    setState({ loading: true, data: null, error: null });
    fetchDrill(request.kind, filters, request.extra || {})
      .then((res) => {
        if (!cancelled) setState({ loading: false, data: res.data, error: null });
      })
      .catch((err) => {
        if (!cancelled) setState({ loading: false, data: null, error: err?.message || "Could not load records" });
      });
    return () => {
      cancelled = true;
    };
  }, [request, filters]);

  const isMatter = state.data?.record_type === "matter";

  const columns = useMemo(() => {
    const areaCol = {
      title: "Practice area",
      dataIndex: "practice_area",
      key: "practice_area",
      render: (v) => (v ? <Tag color={areaColor(v)} style={{ borderRadius: 999 }}>{v}</Tag> : "—"),
    };
    if (isMatter) {
      const isCostKind = ["costs_advanced", "costs_recovered", "costs_outstanding", "costs_at_risk"].includes(request?.kind);
      return [
        { title: "Matter", dataIndex: "name", key: "name", ellipsis: true, render: (v, r) => <strong>{v || r.id}</strong> },
        areaCol,
        { title: "Attorney", dataIndex: "attorney_name", key: "attorney_name", ellipsis: true },
        { title: "Status", dataIndex: "status", key: "status" },
        { title: "Opened", dataIndex: "created_date", key: "created_date", render: fmtDate, sorter: (a, b) => (a.created_date || "").localeCompare(b.created_date || "") },
        ...(request?.kind === "time_to_matter"
          ? [{ title: "Days to open", dataIndex: "days_to_matter", key: "days_to_matter", align: "right", render: (v) => (v === null || v === undefined ? "—" : v), sorter: (a, b) => (a.days_to_matter || 0) - (b.days_to_matter || 0) }]
          : []),
        ...(isCostKind
          ? [
              { title: "Advanced", dataIndex: "cost_advanced", key: "cost_advanced", align: "right", render: (v) => money(v), sorter: (a, b) => (a.cost_advanced || 0) - (b.cost_advanced || 0) },
              { title: "Recovered", dataIndex: "cost_recovered", key: "cost_recovered", align: "right", render: (v) => money(v), sorter: (a, b) => (a.cost_recovered || 0) - (b.cost_recovered || 0) },
              { title: "Balance due", dataIndex: "cost_due", key: "cost_due", align: "right", render: (v) => money(v), sorter: (a, b) => (a.cost_due || 0) - (b.cost_due || 0) },
            ]
          : []),
      ];
    }
    return [
      { title: "Prospect", dataIndex: "name", key: "name", ellipsis: true, render: (v, r) => <strong>{v || r.id}</strong> },
      areaCol,
      { title: "Status", dataIndex: "status", key: "status" },
      { title: "First contact", dataIndex: "created_date", key: "created_date", render: fmtDate, sorter: (a, b) => (a.created_date || "").localeCompare(b.created_date || "") },
      { title: "Days waiting", dataIndex: "days_waiting", key: "days_waiting", align: "right", render: (v) => (v === null || v === undefined ? "—" : Math.round(v)), sorter: (a, b) => (a.days_waiting || 0) - (b.days_waiting || 0) },
      { title: "Signed", dataIndex: "converted", key: "converted", render: (v) => (v ? <Tag color="green">yes</Tag> : <Tag>no</Tag>) },
    ];
  }, [isMatter, request?.kind]);

  const [exporting, setExporting] = useState(false);

  // The drawer shows the first page; the download holds everything the
  // headline counted, so walk the remaining pages before writing the file.
  const exportCsv = async () => {
    if (!state.data?.rows?.length) return;
    const csvColumns = columns.map((c) => ({ title: c.title, key: c.dataIndex }));
    csvColumns.unshift({ title: "Id", key: "id" });
    setExporting(true);
    try {
      const rows = await fetchAllDrillRows(request, filters, state.data);
      const period = request.extra?.month ? request.extra.month : `${filters.start}-${filters.end}`;
      downloadText(`partners-${request.kind}-${period}.csv`, toCsv(rows, csvColumns));
    } catch (err) {
      setState((s) => ({ ...s, error: err?.message || "Could not download every record" }));
    } finally {
      setExporting(false);
    }
  };

  const subtitleBits = [];
  if (request?.label) subtitleBits.push(request.label);
  if (request?.extra?.month) subtitleBits.push(dayjs(`${request.extra.month}-01`).format("MMMM YYYY"));
  else if (!["waiting", "open_caseload", "costs_outstanding", "costs_at_risk"].includes(request?.kind)) subtitleBits.push(`${fmtDate(filters.start)} – ${fmtDate(filters.end)}`);

  return (
    <Drawer
      open={Boolean(request)}
      onClose={onClose}
      width={Math.min(980, typeof window !== "undefined" ? window.innerWidth - 40 : 980)}
      title={
        <div style={{ display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
          <span>{KIND_TITLES[request?.kind] || "Records"}</span>
          <span style={{ color: "#66758A", fontSize: 12, fontWeight: 400 }}>{subtitleBits.join(" · ")}</span>
        </div>
      }
      extra={
        <Button size="small" onClick={exportCsv} loading={exporting} disabled={!state.data?.rows?.length}>
          {exporting ? "Gathering every record…" : "Download CSV"}
        </Button>
      }
      destroyOnClose
    >
      {state.error && <div style={{ color: "#b42318", marginBottom: 12 }}>{state.error}</div>}
      {state.data && state.data.total > state.data.rows.length && (
        <div style={{ color: "#66758A", fontSize: 12, marginBottom: 8 }}>
          Showing the first {state.data.rows.length.toLocaleString()} of {state.data.total.toLocaleString()} — narrow the filters, or download the CSV (it gathers all {state.data.total.toLocaleString()}).
        </div>
      )}
      <Table
        size="small"
        rowKey="id"
        loading={state.loading}
        columns={columns}
        dataSource={state.data?.rows || []}
        pagination={{ pageSize: 25, showSizeChanger: false, showTotal: (t) => `${t.toLocaleString()} records` }}
        onRow={(record) =>
          isMatter
            ? {
                style: { cursor: "pointer" },
                onClick: () => navigate(`/matter/${record.id}`),
              }
            : {}
        }
        locale={{ emptyText: state.loading ? " " : "Nothing behind this number for the current filters." }}
      />
      {isMatter && (
        <Tooltip title="Click a row to open the matter">
          <div style={{ color: "#98A6BA", fontSize: 12, marginTop: 8 }}>Rows open the matter page.</div>
        </Tooltip>
      )}
    </Drawer>
  );
}
